import NextImage from "next/image";
import { useEffect, useState } from "react";
import { Box, Stack, Typography, Divider } from "@mui/material";

import {
  convertDate,
  convertTime,
  convertShortDate,
} from "@/global/utils/dateTimeConvert";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";

import type { EventType } from "@/global/store/data/types";

interface ActiveEventProps {
  event: EventType;
}

export function ActiveEvent(props: ActiveEventProps) {
  const { sm: smDown } = useCssMediaQuery("down");

  if (smDown) {
    return (
      <Stack direction={"row"} alignItems={"center"} spacing={2} py={1}>
        <Box>
          <EventLogo {...props.event} />
        </Box>
        <Box>
          <OpponentName {...props.event} />
          <DateTime {...props.event} />
        </Box>
      </Stack>
    );
  }

  return (
    <Stack direction={"row"} alignItems={"center"} spacing={2}>
      <Box>
        <EventLogo {...props.event} />
      </Box>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={2}
        flexWrap={"wrap"}
      >
        <OpponentName {...props.event} />
        <Divider orientation="vertical" sx={{ height: "32px", width: "2px" }} />
        <DateTime {...props.event} />
      </Stack>
    </Stack>
  );
}

function EventLogo(props: EventType) {
  const { purchaseSeatConfigs } = useTeamConfig();

  if (!props.logoUrl && !props.backgroundUrl) return null;

  return (
    <Box>
      <NextImage
        width={42}
        height={42}
        alt={props.opponentName}
        src={
          (purchaseSeatConfigs?.useBackgroundUrlForEventLogo
            ? props?.backgroundUrl
            : props.logoUrl) as string
        }
        style={{ objectFit: "contain" }}
      />
    </Box>
  );
}

function OpponentName(props: EventType) {
  return (
    <Box>
      <Typography variant={"subtitle1"} color="text.primary" fontWeight={"600"}>
        {props.title}
      </Typography>
    </Box>
  );
}

function DateTime(props: EventType) {
  const { uiConfigs } = useTeamConfig();
  const { sm: smDown } = useCssMediaQuery("down");

  const eventDateTimeReplacementEnabled = Boolean(uiConfigs?.findGames?.eventDateTimeReplacement); // prettier-ignore
  const [dateTime, setDateTime] = useState<string | null>(null);

  useEffect(() => {
    if (eventDateTimeReplacementEnabled) {
      const replacement = uiConfigs?.findGames?.eventDateTimeReplacement?.find(
        (item) => item.eventCode === props.eventCode
      );
      if (replacement) {
        setDateTime(replacement.label);
      }
    }
  }, []);

  return (
    <Stack direction={"row"} alignItems={"center"} spacing={2}>
      <Typography
        variant="body1"
        color="text.secondary"
        fontSize={"0.9rem"}
        fontWeight={"500"}
        lineHeight={1.1}
      >
        {eventDateTimeReplacementEnabled
          ? dateTime
          : smDown
          ? convertShortDate(props.date)
          : convertDate(props.date)}
      </Typography>
      {eventDateTimeReplacementEnabled ? null : (
        <Divider orientation="vertical" sx={{ height: "16px", width: "2px" }} />
      )}
      <Typography
        variant="body1"
        color="text.secondary"
        fontSize={"0.9rem"}
        fontWeight={"500"}
        lineHeight={1.1}
      >
        {eventDateTimeReplacementEnabled ? null : convertTime(props.time)}
      </Typography>
    </Stack>
  );
}
